/**
 * Single source of truth for button CSS class names.
 * Styles live in src/styles/buttons.css — do not duplicate button styling elsewhere.
 */

export const BUTTON_BASE = 'button';

/** Visual variants (maps to modifier classes on .button) */
export const BUTTON_VARIANT = {
    default: '',
    primary: 'button-primary',
    secondary: 'button-secondary',
    accent: 'button-accent',
    danger: 'button-danger',
    cancel: 'button-cancel',
    grey: 'button-grey',
    save: 'button-save',
    /** Icon/text control with no filled background (back nav, toolbar icons) */
    ghost: 'button-ghost',
    /** Minimal text-only control inside banners/toolbars */
    text: 'button-text',
    /** Export / download triggers (formerly .export-button) */
    export: 'button-export',
    /** Filter bar trigger (formerly standalone .filter-button) */
    filter: 'button-filter',
    /** @deprecated Use variant "cancel" — kept for backward-compatible class names */
    copyLink: 'copy-link-button',
    shareModel: 'share-model-button',
} as const;

export const BUTTON_SIZE = {
    sm: 'button-sm',
    md: '',
    lg: 'button-lg',
    large: 'button-large',
} as const;

export const BUTTON_MOD = {
    block: 'button-block',
    iconOnly: 'button-icon-only',
    hideTextMobile: 'text-hidden-on-mobile',
    loading: 'is-loading',
    disabled: 'button-disabled',
    left: 'button-left',
    fullWidth: 'button-full-width',
} as const;

/** Layout wrappers — not applied to the button element itself */
export const BUTTON_GROUP = {
    container: 'button-container',
    group: 'group-buttons',
    modalActions: 'modal-actions',
    confirmation: 'confirmation-dropdown-buttons',
} as const;

/** Dropdown trigger + menu (menu styles remain in dropdown-button.css) */
export const BUTTON_DROPDOWN = {
    container: 'dropdown-button-container',
    menu: 'dropdown-button-menu',
    item: 'dropdown-button-item',
    itemDisabled: 'disabled',
    icon: 'dropdown-button-icon',
    text: 'dropdown-button-text',
    filterLabel: 'dropdown-filter-label',
    filterValue: 'dropdown-filter-value',
} as const;

export const BUTTON_ICON = {
    default: 'button-icon',
    copy: 'copy-icon',
    share: 'share-icon',
    view: 'view-icon',
    new: 'new-icon',
    dropdownItem: 'dropdown-item-icon',
} as const;

export type ButtonVariant = keyof typeof BUTTON_VARIANT;
export type ButtonSize = keyof typeof BUTTON_SIZE;

export type BtnClassOptions = {
    /** Omit or "default" for base filled primary styling */
    variant?: ButtonVariant;
    size?: ButtonSize;
    block?: boolean;
    iconOnly?: boolean;
    hideTextOnMobile?: boolean;
    loading?: boolean;
    /** Applies .button-disabled (visual only; pair with native disabled on the element) */
    visuallyDisabled?: boolean;
    /** Extra modifier keys from BUTTON_MOD or arbitrary legacy classes */
    mods?: string[];
    className?: string;
};

export function btnClass({
    variant = 'default',
    size = 'md',
    block = false,
    iconOnly = false,
    hideTextOnMobile = false,
    loading = false,
    visuallyDisabled = false,
    mods = [],
    className,
}: BtnClassOptions = {}): string {
    const classes: string[] = [BUTTON_BASE];

    const variantClass = BUTTON_VARIANT[variant];
    if (variantClass) {
        classes.push(variantClass);
    }

    if (size !== 'md') {
        classes.push(BUTTON_SIZE[size]);
    }

    if (block) classes.push(BUTTON_MOD.block);
    if (iconOnly) classes.push(BUTTON_MOD.iconOnly);
    if (hideTextOnMobile) classes.push(BUTTON_MOD.hideTextMobile);
    if (loading) classes.push(BUTTON_MOD.loading);
    if (visuallyDisabled) classes.push(BUTTON_MOD.disabled);

    if (mods.length) {
        classes.push(...mods);
    }

    if (className) {
        classes.push(className);
    }

    return classes.filter(Boolean).join(' ');
}

/** Pre-composed class strings for static one-off usage */
export const BUTTON = {
    primary: btnClass({ variant: 'primary' }),
    secondary: btnClass({ variant: 'secondary' }),
    accent: btnClass({ variant: 'accent' }),
    danger: btnClass({ variant: 'danger' }),
    cancel: btnClass({ variant: 'cancel' }),
    grey: btnClass({ variant: 'grey' }),
    save: btnClass({ variant: 'save' }),
    ghost: btnClass({ variant: 'ghost' }),
    text: btnClass({ variant: 'text' }),
    export: btnClass({ variant: 'export' }),
    filter: btnClass({ variant: 'filter' }),
    copyLink: btnClass({ variant: 'copyLink' }),
} as const;

/** Save vs disabled toggle used on settings forms */
export function btnSaveWhen(dirty: boolean): string {
    return btnClass({ variant: dirty ? 'save' : undefined, visuallyDisabled: !dirty });
}
